import type { Mat3, PlanarPose, Point2, Vec3 } from './planar.ts';

export type CameraInstallationProvenance = 'declared-approximate' | 'calibrated-declared-offset';

export const CAMERA_INSTALLATION_LABELS: Record<CameraInstallationProvenance, string> = {
  'declared-approximate': 'declared, approximate',
  'calibrated-declared-offset': 'calibrated + declared offset',
};

/** The minimum geometry consumers need; planar reprojection evidence is intentionally not forged after a move. */
export interface InstalledCameraGeometry {
  focalLengthPx: number;
  principalPointPx: Point2;
  /** Maps CV camera axes (+x right, +y down, +z forward) into the TV frame. */
  rotationCameraToTv: Mat3;
  /** Lens centre in TV metres (+x picture-right, +y up, +z into the room). */
  cameraPositionInTv: Vec3;
  provenance: CameraInstallationProvenance;
}

/** Maps child-frame coordinates into parent-frame coordinates. */
export interface RigidTransform {
  rotation: Mat3;
  translation: Vec3;
}

const radians = (degrees: number): number => (degrees * Math.PI) / 180;

export function multiplyRotation(a: Mat3, b: Mat3): Mat3 {
  const output = new Array<number>(9).fill(0);
  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      for (let index = 0; index < 3; index += 1) {
        output[row * 3 + column]! += a[row * 3 + index]! * b[index * 3 + column]!;
      }
    }
  }
  return output as Mat3;
}

export function rotateVector(rotation: Mat3, vector: Vec3): Vec3 {
  return {
    x: rotation[0] * vector.x + rotation[1] * vector.y + rotation[2] * vector.z,
    y: rotation[3] * vector.x + rotation[4] * vector.y + rotation[5] * vector.z,
    z: rotation[6] * vector.x + rotation[7] * vector.y + rotation[8] * vector.z,
  };
}

/** Compose T_parent_from_middle with T_middle_from_child. */
export function composeRigidTransform(
  parentFromMiddle: RigidTransform,
  middleFromChild: RigidTransform,
): RigidTransform {
  const shifted = rotateVector(parentFromMiddle.rotation, middleFromChild.translation);
  return {
    rotation: multiplyRotation(parentFromMiddle.rotation, middleFromChild.rotation),
    translation: {
      x: parentFromMiddle.translation.x + shifted.x,
      y: parentFromMiddle.translation.y + shifted.y,
      z: parentFromMiddle.translation.z + shifted.z,
    },
  };
}

export function flipInPlaceOffset(): RigidTransform {
  // 180° around the phone's vertical (+y-down) camera axis: T_calibration-camera_from_installed-camera.
  return { rotation: [-1, 0, 0, 0, 1, 0, 0, 0, -1], translation: { x: 0, y: 0, z: 0 } };
}

/**
 * Offset convention: translation is the installed lens in calibration-camera metres.
 * Euler rotations map installed axes into calibration-camera axes, applied X then Y then Z.
 */
export function declaredStandOffset(
  translation: Vec3,
  rotationDegrees: { pitchX: number; yawY: number; rollZ: number },
): RigidTransform {
  if (![translation.x, translation.y, translation.z, rotationDegrees.pitchX, rotationDegrees.yawY, rotationDegrees.rollZ].every(Number.isFinite)) {
    throw new RangeError('Every stand offset value must be finite');
  }
  const x = radians(rotationDegrees.pitchX);
  const y = radians(rotationDegrees.yawY);
  const z = radians(rotationDegrees.rollZ);
  const rx: Mat3 = [1, 0, 0, 0, Math.cos(x), -Math.sin(x), 0, Math.sin(x), Math.cos(x)];
  const ry: Mat3 = [Math.cos(y), 0, Math.sin(y), 0, 1, 0, -Math.sin(y), 0, Math.cos(y)];
  const rz: Mat3 = [Math.cos(z), -Math.sin(z), 0, Math.sin(z), Math.cos(z), 0, 0, 0, 1];
  return { rotation: multiplyRotation(rz, multiplyRotation(ry, rx)), translation };
}

export function composeInstalledCamera(planar: PlanarPose, calibrationCameraFromInstalled: RigidTransform): InstalledCameraGeometry {
  const tvFromCalibrationCamera: RigidTransform = {
    rotation: planar.rotationCameraToTv,
    translation: planar.cameraPositionInTv,
  };
  const tvFromInstalledCamera = composeRigidTransform(tvFromCalibrationCamera, calibrationCameraFromInstalled);
  return {
    focalLengthPx: planar.focalLengthPx,
    principalPointPx: planar.principalPointPx,
    rotationCameraToTv: tvFromInstalledCamera.rotation,
    cameraPositionInTv: tvFromInstalledCamera.translation,
    provenance: 'calibrated-declared-offset',
  };
}

export function focalLengthFromHorizontalFov(imageWidthPx: number, horizontalFovDeg: number): number {
  if (!Number.isFinite(imageWidthPx) || imageWidthPx <= 0) throw new RangeError('Image width must be positive');
  if (!Number.isFinite(horizontalFovDeg) || horizontalFovDeg < 20 || horizontalFovDeg > 150) {
    throw new RangeError('Horizontal field of view must be between 20° and 150°');
  }
  return imageWidthPx / (2 * Math.tan(radians(horizontalFovDeg) / 2));
}

export function declaredBottomCentreCamera(
  panelHeightM: number,
  imageWidthPx: number,
  imageHeightPx: number,
  horizontalFovDeg: number,
): InstalledCameraGeometry {
  if (!Number.isFinite(panelHeightM) || panelHeightM <= 0) throw new RangeError('TV panel height must be positive');
  if (!Number.isFinite(imageHeightPx) || imageHeightPx <= 0) throw new RangeError('Image height must be positive');
  return {
    focalLengthPx: focalLengthFromHorizontalFov(imageWidthPx, horizontalFovDeg),
    principalPointPx: { x: imageWidthPx / 2, y: imageHeightPx / 2 },
    // Upright phone facing away from the TV: image-right is TV-left and image-down is TV-down.
    rotationCameraToTv: [-1, 0, 0, 0, -1, 0, 0, 0, 1],
    cameraPositionInTv: { x: 0, y: -panelHeightM / 2, z: 0 },
    provenance: 'declared-approximate',
  };
}
